import { captureException, withScope } from '@sentry/browser'
import { track } from 'src/utils/tracker'

const hasNativeStreams = typeof globalThis.ReadableStream === 'function'
const hasAsyncIterator =
  hasNativeStreams &&
  typeof ReadableStream.prototype[Symbol.asyncIterator] === 'function'

/**
 * Ensures that Web Streams with async iteration support are available.
 * https://caniuse.com/mdn-api_readablestream_--asynciterator
 *
 * Two scenarios require different strategies:
 *
 * 1. No native ReadableStream (legacy browsers):
 *    Full polyfill via web-streams-polyfill is safe — no native streams to
 *    conflict with.
 *
 * 2. Native ReadableStream exists but lacks Symbol.asyncIterator (Safari ≤26):
 *    Full polyfill MUST NOT be loaded — it replaces global constructors, but
 *    fetch().body remains a native instance. Mixing polyfilled TransformStream
 *    with native ReadableStream causes brand-check failures in pipeThrough(),
 *    breaking React Router's turbo-stream single-fetch decoder.
 *
 *    Instead, patch only Symbol.asyncIterator onto the native prototype using
 *    the public reader API (getReader/read/releaseLock). This matches the
 *    spec-defined behavior for the actual usage (full consumption of blob
 *    streams for SHA256 hashing).
 */
export async function ensureWebStreamsPolyfill(): Promise<void> {
  if (hasAsyncIterator) return

  if (!hasNativeStreams) {
    track('polyfill:web-streams:full')
    await import('web-streams-polyfill/es2018')
    return
  }

  track('polyfill:web-streams:async-iterator')

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(ReadableStream.prototype as any)[Symbol.asyncIterator] = async function* (
      this: ReadableStream,
    ) {
      const reader = this.getReader()
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) return
          yield value
        }
      } finally {
        reader.releaseLock()
      }
    }
  } catch (err) {
    withScope(scope => {
      scope.setTag('polyfill', 'web-streams-async-iterator')
      captureException(err)
    })
  }
}
